import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Autocomplete,
  Pagination,
  IconButton,
  Tooltip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  InputAdornment,
  useTheme,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LockIcon from '@mui/icons-material/Lock';
import SecurityIcon from '@mui/icons-material/Security';
import PublicIcon from '@mui/icons-material/Public';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import CategoryIcon from '@mui/icons-material/Category';
import { Helmet } from 'react-helmet';
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import BusinessIcon from '@mui/icons-material/Business';
import SearchIcon from '@mui/icons-material/Search';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import CloseIcon from '@mui/icons-material/Close';
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import BoltIcon from "@mui/icons-material/Bolt";
import InventoryIcon from "@mui/icons-material/Inventory";
import SortIcon from "@mui/icons-material/Sort";

import { useUser } from "../context/UserContext";
import SectionLoader from "../components/SectionLoader";
import Toast from "../components/Toast";
import ArchiveManager from "../utils/archiveManager";
import ConfirmDialog from "../components/ConfirmDialog";
import DOMPurify from "dompurify";

const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
};

const stringToColor = (string) => {
  if (!string) return 'linear-gradient(135deg, #0d9488 0%, #115e59 100%)';
  let hash = 0;
  for (let i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  const gradients = [
    'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
    'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
    'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
    'linear-gradient(135deg, #db2777 0%, #be185d 100%)',
    'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
    'linear-gradient(135deg, #059669 0%, #047857 100%)',
    'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)',
  ];
  return gradients[Math.abs(hash) % gradients.length];
};

const getInitials = (firstName, lastName) => {
  const f = firstName ? firstName.trim().charAt(0).toUpperCase() : '';
  const l = lastName ? lastName.trim().charAt(0).toUpperCase() : '';
  return `${f}${l}` || 'P';
};

const PRIORITY_WEIGHTS = {
  critical: 1,
  high: 2,
  medium: 3,
  low: 4,
};

const getPriorityWeight = (priority) => {
  if (!priority) return 5;
  const p = String(priority).toLowerCase().trim();
  return PRIORITY_WEIGHTS[p] || 5;
};

// ── Compact Status Tab Component ──
function StatCard({ label, count, IconComponent, active = false, onClick, colorTheme = "teal" }) {
  const muiTheme = useTheme();
  const isLight = muiTheme.palette.mode === 'light';

  const themeConfig = {
    teal: {
      accent: '#0D9488',
      bgLight: '#F0FDFA',
      activeBgLight: '#CCFBF1',
      darkAccent: '#2DD4BF',
      activeBgDark: 'rgba(13, 148, 136, 0.25)',
    },
    amber: {
      accent: '#D97706',
      bgLight: '#FFFBEB',
      activeBgLight: '#FEF3C7',
      darkAccent: '#FBBF24',
      activeBgDark: 'rgba(217, 119, 6, 0.25)',
    },
    blue: {
      accent: '#0284C7',
      bgLight: '#F0F9FF',
      activeBgLight: '#E0F2FE',
      darkAccent: '#38BDF8',
      activeBgDark: 'rgba(2, 132, 199, 0.25)',
    },
    red: {
      accent: '#DC2626',
      bgLight: '#FEF2F2',
      activeBgLight: '#FEE2E2',
      darkAccent: '#F87171',
      activeBgDark: 'rgba(220, 38, 38, 0.25)',
    },
    slate: {
      accent: '#475569',
      bgLight: '#F8FAFC',
      activeBgLight: '#E2E8F0',
      darkAccent: '#94A3B8',
      activeBgDark: 'rgba(148, 163, 184, 0.2)',
    }
  }[colorTheme] || {
    accent: '#0D9488',
    bgLight: '#F0FDFA',
    activeBgLight: '#CCFBF1',
    darkAccent: '#2DD4BF',
    activeBgDark: 'rgba(13, 148, 136, 0.25)',
  };

  const currentAccent = isLight ? themeConfig.accent : themeConfig.darkAccent;

  return (
    <Box
      onClick={onClick}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1.1,
        px: 1.6,
        py: 0.85,
        borderRadius: "11px",
        cursor: onClick ? "pointer" : "default",
        userSelect: "none",
        transition: "all 0.18s cubic-bezier(0.4, 0, 0.2, 1)",
        bgcolor: active
          ? (isLight ? themeConfig.activeBgLight : themeConfig.activeBgDark)
          : (isLight ? "#FFFFFF" : "rgba(255, 255, 255, 0.04)"),
        border: active
          ? `2px solid ${currentAccent}`
          : (isLight ? "1.5px solid #CBD5E1" : "1px solid rgba(255, 255, 255, 0.12)"),
        boxShadow: active
          ? (isLight ? `0 2px 10px ${themeConfig.accent}25` : "0 2px 8px rgba(0,0,0,0.3)")
          : (isLight ? "0 1px 2px rgba(15, 23, 42, 0.04)" : "none"),
        '&:hover': onClick ? {
          transform: "translateY(-1px)",
          borderColor: currentAccent,
          bgcolor: isLight ? themeConfig.bgLight : "rgba(255,255,255,0.08)",
        } : {},
      }}
    >
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: "7px",
          bgcolor: isLight ? themeConfig.bgLight : "rgba(255,255,255,0.08)",
          color: currentAccent,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <IconComponent sx={{ fontSize: 16 }} />
      </Box>

      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          fontSize: "0.82rem",
          color: active ? (isLight ? "#0F172A" : "#FFFFFF") : (isLight ? "#475569" : "rgba(255,255,255,0.85)"),
          lineHeight: 1,
        }}
      >
        {label}
      </Typography>

      <Box
        sx={{
          px: 0.8,
          py: 0.2,
          borderRadius: "6px",
          bgcolor: active ? currentAccent : (isLight ? "#F1F5F9" : "rgba(255,255,255,0.1)"),
          color: active ? "#FFFFFF" : (isLight ? "#0F172A" : "#FFFFFF"),
          fontWeight: 800,
          fontSize: "0.78rem",
          minWidth: 20,
          textAlign: "center",
          lineHeight: 1.2,
        }}
      >
        {count}
      </Box>
    </Box>
  );
}

export default function PrivatePrayersList({ hideHeader = false, initialVisibility = "all" }) {
  const muiTheme = useTheme();
  const isLight = muiTheme.palette.mode === 'light';

  // Filters & State
  const [filterVisibility, setFilterVisibility] = useState(initialVisibility || "all");

  // State: Add Private Prayer Dialog & Form
  const [openDialog, setOpenDialog] = useState(false);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [priority, setPriority] = useState("medium");
  const [requestText, setRequestText] = useState("");
  const [privateAudience, setPrivateAudience] = useState("private"); // 'private' (Pastoral) or 'elders'
  const [submitAnon, setSubmitAnon] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (initialVisibility) {
      setFilterVisibility(initialVisibility);
    }
  }, [initialVisibility]);
  const [filterCategory, setFilterCategory] = useState("All Categories");
  const [filterSort, setFilterSort] = useState("");
  const [filterPriority, setFilterPriority] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [organizations, setOrganizations] = useState([]);
  const [organization, setOrganization] = useState(null);
  const orgOptions = [{ id: 0, org_name: "All Organizations" }, ...organizations];

  // Sorting state (priority_desc | priority_asc | date_desc | date_asc | title_asc | title_desc | requester_asc | requester_desc | org_asc | status_asc)
  const [sortBy, setSortBy] = useState("date_desc");

  const handleRequestSort = (property) => {
    let nextSort;
    if (sortBy === `${property}_desc`) {
      nextSort = `${property}_asc`;
    } else if (sortBy === `${property}_asc`) {
      nextSort = `${property}_desc`;
    } else {
      nextSort = (property === 'title' || property === 'requester' || property === 'org') ? `${property}_asc` : `${property}_desc`;
    }
    setSortBy(nextSort);
    setPage(1);
  };

  const getSortLabel = (sortVal) => {
    switch (sortVal) {
      case 'priority_desc': return 'Priority (Critical First)';
      case 'priority_asc': return 'Priority (Lowest First)';
      case 'date_desc': return 'Date: Newest First';
      case 'date_asc': return 'Date: Oldest First';
      case 'title_asc': return 'Title: A → Z';
      case 'title_desc': return 'Title: Z → A';
      case 'requester_asc': return 'Requester: A → Z';
      case 'requester_desc': return 'Requester: Z → A';
      case 'org_asc': return 'Organization: A → Z';
      case 'status_asc': return 'Status: Pending First';
      case 'status_desc': return 'Status: Resolved First';
      default: return 'Priority (Critical First)';
    }
  };

  // Pagination state (client-side driven over priority-ordered master set)
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  // Master Data
  const [categories, setCategories] = useState([]);
  const [allPrayerRequests, setAllPrayerRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  // Stats
  const [ActivePrayers, SetActivePrayers] = useState(0);
  const [PendingPrayers, SetPendingPrayers] = useState(0);
  const [AnsweredPrayers, SetAnsweredPrayers] = useState(0);
  const [DeclinePrayers, SetADeclinePrayers] = useState(0);

  // View modal state
  const [selectedPrayer, setSelectedPrayer] = useState(null);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [modalPriority, setModalPriority] = useState("medium");

  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const { user, isSwitched, effectiveOrgId, orgViewMode } = useUser();
  const isSwitchedOrgMode = Boolean(
    isSwitched ||
    orgViewMode ||
    effectiveOrgId ||
    user?.is_switched_from_super_admin ||
    sessionStorage.getItem('gpn_sa_token') ||
    localStorage.getItem('gpn_sa_token') ||
    sessionStorage.getItem('gpn_org_view_mode') ||
    localStorage.getItem('gpn_org_view_mode')
  );
  const showOrganizationColumn = Boolean(user?.role === 1 && !isSwitchedOrgMode && !user?.org_id);

  // Archived
  const [archivedList, setArchivedList] = useState(() => ArchiveManager.getArchivedItems('private-prayers'));
  const [filterActiveTab, setFilterActiveTab] = useState('all');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });

  const API_URL = process.env.REACT_APP_API_URL || 'http://45.194.3.128:4000/api/admin/';

  // 1. Fetch Categories & Initial stats
  useEffect(() => {
    fetchCategories();
  }, [effectiveOrgId, organization, user?.org_id]);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const storedOrgMode = sessionStorage.getItem('gpn_org_view_mode');
      const orgViewMode = storedOrgMode ? JSON.parse(storedOrgMode) : null;
      const targetOrg = orgViewMode?.orgId || effectiveOrgId || (organization && organization.id !== 0 ? organization?.id : (isSwitched || user?.role !== 1 ? user?.org_id : null));

      const url = targetOrg
        ? `${API_URL}all-prayer-categories?type=private&organization=${targetOrg}`
        : `${API_URL}all-prayer-categories?type=private`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();

      if (!response.ok || data.success === false) {
        if (data.message === 'Invalid token') {
          localStorage.removeItem('token');
          window.location.href = '/login';
          return;
        }
        return;
      }

      const rawCategories = data.data.categories || [];
      const sortedCats = [...rawCategories].sort((a, b) => {
        const nameA = (a.name || '').trim().toLowerCase();
        const nameB = (b.name || '').trim().toLowerCase();
        if (nameA === 'others' || nameA === 'other') return 1;
        if (nameB === 'others' || nameB === 'other') return -1;
        return nameA.localeCompare(nameB);
      });
      setCategories(sortedCats);
      setOrganizations(data.data.organisations || []);
      SetActivePrayers(data.data.activeprayerscount ?? 0);
      SetPendingPrayers(data.data.pendingprayerscount ?? 0);
      SetAnsweredPrayers(data.data.answeredprayerscount ?? 0);
      SetADeclinePrayers(data.data.rejectedprayerscount ?? 0);
    } catch (err) {
      console.error('Error in fetchCategories (PrivatePrayersList):', err);
    }
  };

  // 2. Comprehensive Multi-Page Fetcher for Global Priority Sorting
  useEffect(() => {
    fetchAllPrivatePrayers();
  }, [filterVisibility, filterCategory, filterSort, organization, fromDate, toDate, effectiveOrgId, user?.org_id]);

  const fetchAllPrivatePrayers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const storedOrgMode = sessionStorage.getItem('gpn_org_view_mode');
      const orgViewMode = storedOrgMode ? JSON.parse(storedOrgMode) : null;
      const isSuperAdmin = user?.role === 1 && !user?.is_switched_from_super_admin && !orgViewMode;
      
      let targetOrg = null;
      if (organization && organization.id && organization.id !== 0) {
        targetOrg = organization.id;
      } else if (orgViewMode?.orgId && orgViewMode.orgId !== 1) {
        targetOrg = orgViewMode.orgId;
      } else if (!isSuperAdmin && user?.org_id && user.org_id !== 1) {
        targetOrg = user.org_id;
      }

      const basePayload = {
        visibility: filterVisibility !== "all" ? filterVisibility : undefined,
        category: filterCategory,
        status: filterSort,
        organization: targetOrg || null,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
      };

      const response = await fetch(`${API_URL}private-prayer-list`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...basePayload, page: 1 })
      });
      const data = await response.json();

      if (!response.ok || data.success === false) {
        if (data.message === 'Invalid token') {
          localStorage.removeItem('token');
          window.location.href = '/login';
          return;
        }
      }

      let aggregated = Array.isArray(data.data) ? [...data.data] : [];
      const totalPagesServer = data.pagination?.totalPages || 1;

      if (totalPagesServer > 1) {
        const fetchPromises = [];
        for (let p = 2; p <= Math.min(totalPagesServer, 15); p++) {
          fetchPromises.push(
            fetch(`${API_URL}private-prayer-list`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ ...basePayload, page: p })
            })
            .then(res => res.json())
            .then(resData => (resData && resData.success && Array.isArray(resData.data)) ? resData.data : [])
            .catch(() => [])
          );
        }
        const extraPages = await Promise.all(fetchPromises);
        extraPages.forEach(batch => {
          aggregated = aggregated.concat(batch);
        });
      }

      const map = new Map();
      aggregated.forEach(item => {
        if (item && item.id) map.set(String(item.id), item);
      });
      setAllPrayerRequests(Array.from(map.values()));
      setPage(1);
    } catch (err) {
      console.error("Error fetching private prayers:", err);
    } finally {
      setLoading(false);
    }
  };

  // 3. Priority Counts Computed in Real-Time
  const priorityCounts = useMemo(() => {
    const counts = { all: 0, critical: 0, high: 0, medium: 0, standard: 0 };
    const list = filterActiveTab === "archived"
      ? archivedList
      : allPrayerRequests.filter(p => !archivedList.some(a => String(a.id) === String(p.id)));

    counts.all = list.length;
    list.forEach(p => {
      const imp = String(p.importance || "").toLowerCase().trim();
      if (imp === "critical") counts.critical += 1;
      else if (imp === "high") counts.high += 1;
      else if (imp === "medium") counts.medium += 1;
      else counts.standard += 1;
    });
    return counts;
  }, [allPrayerRequests, archivedList, filterActiveTab]);

  // 4. MASTER SORTING & FILTERING: CRITICAL -> HIGH -> MEDIUM -> LOW -> STANDARD
  const filteredAndSortedPrayers = useMemo(() => {
    let list = filterActiveTab === "archived"
      ? archivedList
      : allPrayerRequests.filter(p => !archivedList.some(a => String(a.id) === String(p.id)));

    // Priority filter
    if (filterPriority !== "all") {
      if (filterPriority === "standard") {
        list = list.filter(p => !p.importance || String(p.importance).toLowerCase() === 'low');
      } else {
        list = list.filter(p => String(p.importance || '').toLowerCase() === filterPriority);
      }
    }

    // Search query
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      list = list.filter(p => {
        const name = `${p.app_users?.first_name || ''} ${p.app_users?.last_name || ''}`.toLowerCase();
        const title = (p.title || '').toLowerCase();
        const desc = stripHtml(p.description || '').toLowerCase();
        const cat = (p.categories?.name || '').toLowerCase();
        const org = (p.origanisation?.org_name || p.organizations?.org_name || '').toLowerCase();
        return name.includes(term) || title.includes(term) || desc.includes(term) || cat.includes(term) || org.includes(term);
      });
    }

    // DYNAMIC SORTING: Supports Priority (Critical first / Lowest first), Date, Title, Requester, Organization, and Status
    const sorted = [...list].sort((a, b) => {
      if (sortBy === 'priority_desc') {
        const weightA = getPriorityWeight(a.importance);
        const weightB = getPriorityWeight(b.importance);
        if (weightA !== weightB) return weightA - weightB;
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      }
      if (sortBy === 'priority_asc') {
        const weightA = getPriorityWeight(a.importance);
        const weightB = getPriorityWeight(b.importance);
        if (weightA !== weightB) return weightB - weightA;
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      }
      if (sortBy === 'date_desc') {
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      }
      if (sortBy === 'date_asc') {
        return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      }
      if (sortBy === 'title_asc') {
        const titleA = (a.title || '').toLowerCase();
        const titleB = (b.title || '').toLowerCase();
        return titleA.localeCompare(titleB);
      }
      if (sortBy === 'title_desc') {
        const titleA = (a.title || '').toLowerCase();
        const titleB = (b.title || '').toLowerCase();
        return titleB.localeCompare(titleA);
      }
      if (sortBy === 'requester_asc' || sortBy === 'requester_desc') {
        const nameA = (a.is_anonymous === 1 || a.is_anonymous === '1')
          ? 'Anonymous'
          : `${a.app_users?.first_name || ''} ${a.app_users?.last_name || ''}`.trim() || 'Guest';
        const nameB = (b.is_anonymous === 1 || b.is_anonymous === '1')
          ? 'Anonymous'
          : `${b.app_users?.first_name || ''} ${b.app_users?.last_name || ''}`.trim() || 'Guest';
        const comp = nameA.toLowerCase().localeCompare(nameB.toLowerCase());
        return sortBy === 'requester_asc' ? comp : -comp;
      }
      if (sortBy === 'org_asc') {
        const orgA = (a.origanisation?.org_name || a.organizations?.org_name || '').toLowerCase();
        const orgB = (b.origanisation?.org_name || b.organizations?.org_name || '').toLowerCase();
        return orgA.localeCompare(orgB);
      }
      if (sortBy === 'status_asc' || sortBy === 'status_desc') {
        const statusOrder = (item) => {
          if (item.deleted_at !== null) return 5;
          if (item.is_approved === "0") return 1; // Pending
          if (item.is_approved === "1" && item.is_answerd !== 1) return 2; // Active
          if (item.is_answerd === 1) return 3; // Answered
          if (item.is_approved === "2") return 4; // Declined
          return 6;
        };
        const orderA = statusOrder(a);
        const orderB = statusOrder(b);
        return sortBy === 'status_asc' ? orderA - orderB : orderB - orderA;
      }
      // Default: priority_desc
      const weightA = getPriorityWeight(a.importance);
      const weightB = getPriorityWeight(b.importance);
      if (weightA !== weightB) return weightA - weightB;
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });

    return sorted;
  }, [allPrayerRequests, archivedList, filterActiveTab, filterPriority, searchTerm, sortBy]);

  // Client-Side Paginated Slices
  const totalDisplayPages = Math.ceil(filteredAndSortedPrayers.length / rowsPerPage) || 1;
  const paginatedPrayers = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredAndSortedPrayers.slice(start, start + rowsPerPage);
  }, [filteredAndSortedPrayers, page, rowsPerPage]);

  // Quick Approve / Decline
  const handleApprovePrayer = async (id, status) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}approve-prayer`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id, status }),
      });
      const data = await response.json();
      if (!response.ok || data.success === false) {
        if (data.message === 'Invalid token') {
          localStorage.removeItem('token');
          window.location.href = '/login';
          return;
        }
      }

      setToast({
        open: true,
        message: status === "1" ? "Private prayer approved successfully!" : "Private prayer declined successfully!",
        severity: status === "1" ? "success" : "info"
      });

      setAllPrayerRequests(prev => prev.map(p => {
        if (String(p.id) === String(id)) {
          return { ...p, is_approved: status, is_answerd: 0 };
        }
        return p;
      }));
      fetchCategories();
    } catch (err) {
      setToast({ open: true, message: "Failed to update prayer status.", severity: "error" });
    }
  };

  const handleConfirmDeclinePrayer = (id) => {
    setConfirmDialog({
      open: true,
      title: "Decline Private Prayer",
      message: "Are you sure you want to decline this private prayer request?",
      onConfirm: () => {
        setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
        if (openViewDialog) handleViewDialogClose();
        handleApprovePrayer(id, "2");
      }
    });
  };

  // Archive / Restore / Permanent Delete
  const handleArchive = (prayer) => {
    setConfirmDialog({
      open: true,
      title: "Archive Private Prayer",
      message: "Are you sure you want to archive this private prayer? It will no longer be visible in the mobile app until restored.",
      onConfirm: () => {
        ArchiveManager.archiveItem('private-prayers', prayer);
        setArchivedList(ArchiveManager.getArchivedItems('private-prayers'));
        setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
        if (openViewDialog) handleViewDialogClose();
        setToast({ open: true, message: "Prayer archived and hidden from mobile app.", severity: "info" });
      }
    });
  };

  const handleRestore = (prayerId) => {
    ArchiveManager.restoreItem('private-prayers', prayerId);
    setArchivedList(ArchiveManager.getArchivedItems('private-prayers'));
    setToast({ open: true, message: "Prayer restored to active status!", severity: "success" });
  };

  const handlePermanentDelete = (prayerId) => {
    setConfirmDialog({
      open: true,
      title: "Permanently Delete Prayer",
      message: "Are you sure you want to permanently delete this archived prayer? This cannot be undone.",
      onConfirm: () => {
        ArchiveManager.deletePermanently('private-prayers', prayerId);
        setArchivedList(ArchiveManager.getArchivedItems('private-prayers'));
        setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
        if (openViewDialog) handleViewDialogClose();
        setToast({ open: true, message: "Prayer permanently removed.", severity: "success" });
      }
    });
  };

  const handleUpdatePriority = async (prayerId, newPriority) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}update-prayer-priority`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: prayerId,
          priority: newPriority,
        }),
      });
      const data = await response.json();
      if (data.success) {
        setModalPriority(newPriority);
        if (selectedPrayer) {
          setSelectedPrayer(prev => ({ ...prev, importance: newPriority }));
        }
        setAllPrayerRequests(prev => prev.map(p => {
          if (String(p.id) === String(prayerId)) {
            return { ...p, importance: newPriority };
          }
          return p;
        }));
        setToast({ open: true, message: `Priority set to ${newPriority.toUpperCase()}!`, severity: "success" });
      } else {
        setToast({ open: true, message: data.message || "Failed to update priority", severity: "error" });
      }
    } catch (err) {
      setToast({ open: true, message: "Error updating priority", severity: "error" });
    }
  };

  // Submit Private Prayer
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!category || !title.trim() || !requestText.trim()) {
      setFormError("Please fill in all required fields.");
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const storedOrgMode = sessionStorage.getItem('gpn_org_view_mode');
      const orgViewMode = storedOrgMode ? JSON.parse(storedOrgMode) : null;
      const targetOrg = orgViewMode?.orgId || effectiveOrgId || (organization && organization.id !== 0 ? organization?.id : (user?.org_id || 1));

      const payload = {
        category: category,
        category_id: category,
        title: title.trim(),
        description: requestText.trim(),
        priority: priority,
        importance: priority,
        organization_id: targetOrg,
        is_anonymous: submitAnon ? 1 : 0,
        visibility: privateAudience,
        checked: privateAudience === 'elders' ? 2 : 1,
      };

      const response = await fetch(`${API_URL}add-private-prayer`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (response.ok && data.success !== false) {
        setToast({ open: true, message: "Confidential prayer request submitted successfully!", severity: "success" });
        setOpenDialog(false);
        setTitle("");
        setRequestText("");
        setCategory("");
        setPriority("medium");
        setPrivateAudience("private");
        setSubmitAnon(false);
        setFormError("");
        fetchAllPrivatePrayers();
        fetchCategories();
      } else {
        setFormError(data.message || data.error || "Failed to submit prayer request.");
      }
    } catch (err) {
      setFormError("Network error. Please try again.");
    }
  };

  // View Modal handlers
  const handleViewPrayer = (req) => {
    setSelectedPrayer(req);
    setModalPriority(req.importance || "medium");
    setOpenViewDialog(true);
  };

  const handleViewDialogClose = () => {
    setOpenViewDialog(false);
    setSelectedPrayer(null);
  };

  const handleStatCardClick = (targetStatus) => {
    if (filterSort === targetStatus) {
      setFilterSort("");
    } else {
      setFilterSort(targetStatus);
    }
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilterCategory("All Categories");
    setFilterSort("");
    setFilterPriority("all");
    setSortBy("priority_desc");
    setFromDate("");
    setToDate("");
    setSearchTerm("");
    setOrganization(null);
    setFilterActiveTab("all");
    setPage(1);
  };

  // Status badge config
  const getStatusBadge = (prayer) => {
    const statusLabel =
      prayer.deleted_at !== null
        ? "Deleted"
        : prayer.is_answerd === 1
          ? "Answered"
          : prayer.is_approved === "0"
            ? "Pending"
            : prayer.is_approved === "1"
              ? "Active"
              : prayer.is_approved === "2"
                ? "Declined"
                : "Unknown";

    const lightStyles = {
      "Active": { bg: "#ECFDF5", border: "#A7F3D0", text: "#047857", dot: "#10B981" },
      "Pending": { bg: "#FFFBEB", border: "#FDE68A", text: "#B45309", dot: "#F59E0B" },
      "Answered": { bg: "#F0F9FF", border: "#BAE6FD", text: "#0369A1", dot: "#0EA5E9" },
      "Declined": { bg: "#FEF2F2", border: "#FECACA", text: "#B91C1C", dot: "#EF4444" },
      "Deleted": { bg: "#F1F5F9", border: "#CBD5E1", text: "#475569", dot: "#94A3B8" },
      "Unknown": { bg: "#F8FAFC", border: "#E2E8F0", text: "#0F172A", dot: "#64748B" },
    }[statusLabel];

    const darkStyles = {
      "Active": { bg: "rgba(16, 185, 129, 0.15)", border: "rgba(16, 185, 129, 0.45)", text: "#34D399", dot: "#10B981" },
      "Pending": { bg: "rgba(245, 158, 11, 0.15)", border: "rgba(245, 158, 11, 0.45)", text: "#FBBF24", dot: "#F59E0B" },
      "Answered": { bg: "rgba(6, 182, 212, 0.15)", border: "rgba(6, 182, 212, 0.45)", text: "#38BDF8", dot: "#06B6D4" },
      "Declined": { bg: "rgba(239, 68, 68, 0.15)", border: "rgba(239, 68, 68, 0.45)", text: "#F87171", dot: "#EF4444" },
      "Deleted": { bg: "rgba(107, 114, 128, 0.2)", border: "rgba(107, 114, 128, 0.45)", text: "#9CA3AF", dot: "#6B7280" },
      "Unknown": { bg: "rgba(255,255,255,0.1)", border: "rgba(255,255,255,0.2)", text: "#FFFFFF", dot: "#FFFFFF" },
    }[statusLabel];

    const style = isLight ? lightStyles : darkStyles;

    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.8,
          px: 1.2,
          py: 0.4,
          borderRadius: "8px",
          bgcolor: style.bg,
          border: `1.5px solid ${style.border}`,
          color: style.text,
          fontSize: "0.76rem",
          fontWeight: 800,
          letterSpacing: "0.4px",
          textTransform: "uppercase",
        }}
      >
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            bgcolor: style.dot,
            boxShadow: `0 0 6px ${style.dot}`,
          }}
        />
        <span>{statusLabel}</span>
      </Box>
    );
  };

  // Priority Badge Component with Glowing Indicator
  const getPriorityBadge = (priorityVal) => {
    const val = String(priorityVal || "standard").toLowerCase().trim();
    const map = {
      critical: {
        label: "Critical",
        icon: WhatshotIcon,
        bg: isLight ? "#FEF2F2" : "rgba(239, 68, 68, 0.2)",
        border: isLight ? "#FCA5A5" : "rgba(239, 68, 68, 0.55)",
        color: isLight ? "#B91C1C" : "#F87171",
        dot: "#EF4444",
        glow: "0 0 8px rgba(239, 68, 68, 0.5)",
      },
      high: {
        label: "High",
        icon: BoltIcon,
        bg: isLight ? "#FFFBEB" : "rgba(245, 158, 11, 0.2)",
        border: isLight ? "#FCD34D" : "rgba(245, 158, 11, 0.55)",
        color: isLight ? "#B45309" : "#FBBF24",
        dot: "#F59E0B",
        glow: "0 0 8px rgba(245, 158, 11, 0.45)",
      },
      medium: {
        label: "Medium",
        icon: null,
        bg: isLight ? "#F0FDFA" : "rgba(13, 148, 136, 0.18)",
        border: isLight ? "#99F6E4" : "rgba(13, 148, 136, 0.5)",
        color: isLight ? "#0F766E" : "#5EEAD4",
        dot: "#0D9488",
        glow: "none",
      },
      low: {
        label: "Low",
        icon: null,
        bg: isLight ? "#F1F5F9" : "rgba(148, 163, 184, 0.15)",
        border: isLight ? "#CBD5E1" : "rgba(148, 163, 184, 0.35)",
        color: isLight ? "#475569" : "#94A3B8",
        dot: "#64748B",
        glow: "none",
      },
      standard: {
        label: "Standard",
        icon: null,
        bg: isLight ? "#F8FAFC" : "rgba(148, 163, 184, 0.12)",
        border: isLight ? "#E2E8F0" : "rgba(148, 163, 184, 0.25)",
        color: isLight ? "#64748B" : "#94A3B8",
        dot: "#94A3B8",
        glow: "none",
      }
    };

    const cfg = map[val] || map.standard;
    const IconComp = cfg.icon;

    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.6,
          px: 1.1,
          py: 0.35,
          borderRadius: "8px",
          bgcolor: cfg.bg,
          border: `1.5px solid ${cfg.border}`,
          color: cfg.color,
          fontSize: "0.74rem",
          fontWeight: 800,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          boxShadow: cfg.glow !== "none" ? cfg.glow : "none",
        }}
      >
        {IconComp ? (
          <IconComp sx={{ fontSize: 14, color: cfg.dot }} />
        ) : (
          <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: cfg.dot }} />
        )}
        <span>{cfg.label}</span>
      </Box>
    );
  };

  return (
    <>
      <Helmet>
        <title>Private Prayers List - GPN</title>
      </Helmet>

      <Box sx={{ p: hideHeader ? 0 : { xs: 1, sm: 2, md: 3 }, pt: hideHeader ? 0 : undefined }}>
        {/* Header (optional) */}
        {!hideHeader && (
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
            <Typography variant="h5" fontWeight="bold">
              Private Prayers List
            </Typography>
          </Box>
        )}

        {/* ── UNIFIED COMPACT TOOLBAR ── */}
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            px: 2,
            mb: 2,
            borderRadius: "12px",
            background: isLight ? "#FFFFFF" : "rgba(255, 255, 255, 0.04)",
            border: isLight ? "1.5px solid #CBD5E1" : "1px solid rgba(76, 161, 175, 0.25)",
            boxShadow: isLight ? "0 2px 8px rgba(15, 23, 42, 0.04)" : "none",
            display: "flex",
            flexDirection: "column",
            gap: 1.2,
          }}
        >
          {/* Row 1: Search Bar (Left) + Actions (Right) */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1.2,
            }}
          >
            {/* Search Input */}
            <Box sx={{ flex: "1 1 260px", maxWidth: { xs: "100%", md: 420 } }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search requester, title, details..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.5)", fontSize: 19 }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => { setSearchTerm(""); setPage(1); }}>
                        <CloseIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "8px",
                    bgcolor: isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.05)",
                    border: isLight ? "1px solid #CBD5E1" : "1px solid rgba(255, 255, 255, 0.15)",
                    color: isLight ? "#0F172A" : "#FFFFFF",
                    fontSize: "0.85rem",
                    '& fieldset': { border: 'none' },
                    '&:hover': { bgcolor: isLight ? "#FFFFFF" : "rgba(255, 255, 255, 0.08)" },
                    '&.Mui-focused': {
                      bgcolor: isLight ? "#FFFFFF" : "rgba(255, 255, 255, 0.08)",
                      boxShadow: '0 0 0 2px #0D9488',
                    },
                  },
                }}
              />
            </Box>

            {/* Right: Private Closet Badge & Add Prayer Button */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: { xs: 0, sm: "auto" } }}>
              <Chip
                icon={<LockIcon sx={{ fontSize: '15px !important', color: isLight ? '#0D9488 !important' : '#2DD4BF !important' }} />}
                label="Private Closet Archive"
                size="small"
                sx={{
                  bgcolor: isLight ? '#F0FDFA' : 'rgba(13, 148, 136, 0.15)',
                  border: isLight ? '1px solid #99F6E4' : '1px solid rgba(13, 148, 136, 0.35)',
                  color: isLight ? '#0F766E' : '#5EEAD4',
                  fontWeight: 700,
                  fontSize: '0.78rem',
                }}
              />

              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon sx={{ fontSize: 17 }} />}
                onClick={() => setOpenDialog(true)}
                sx={{
                  borderRadius: "8px",
                  background: "linear-gradient(135deg, #0D9488 0%, #14B8A6 100%)",
                  color: "#FFFFFF",
                  fontWeight: 700,
                  fontSize: "0.82rem",
                  textTransform: "none",
                  py: 0.65,
                  px: 1.8,
                  boxShadow: "0 2px 8px rgba(13, 148, 136, 0.25)",
                  '&:hover': {
                    background: "linear-gradient(135deg, #0F766E 0%, #0D9488 100%)",
                  }
                }}
              >
                Add Prayer Request
              </Button>
            </Box>
          </Box>

          {/* Row 2: Compact Filters & Sorting */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
              pt: 0.8,
              borderTop: isLight ? "1px solid #F1F5F9" : "1px solid rgba(255, 255, 255, 0.08)",
            }}
          >
            {/* Status Dropdown with Live Counts */}
            <FormControl size="small" sx={{ minWidth: 145 }}>
              <InputLabel sx={{ color: isLight ? '#475569' : undefined, fontWeight: 700, fontSize: "0.82rem" }}>
                Status
              </InputLabel>
              <Select
                value={filterActiveTab === "archived" ? "archived" : filterSort}
                label="Status"
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "archived") {
                    setFilterActiveTab("archived");
                    setFilterSort("");
                  } else {
                    setFilterActiveTab("all");
                    setFilterSort(val);
                  }
                  setPage(1);
                }}
                sx={{
                  borderRadius: "8px",
                  bgcolor: isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.05)",
                  color: isLight ? "#0F172A" : "#FFFFFF",
                  fontWeight: 600,
                  fontSize: "0.82rem",
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: isLight ? '#CBD5E1' : 'rgba(255, 255, 255, 0.15)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0D9488' },
                }}
              >
                <MenuItem value="" sx={{ fontWeight: 600 }}>All Statuses ({allPrayerRequests.length})</MenuItem>
                <MenuItem value="1" sx={{ fontWeight: 600 }}>🟢 Active ({ActivePrayers})</MenuItem>
                <MenuItem value="0" sx={{ fontWeight: 600 }}>🟡 Pending ({PendingPrayers})</MenuItem>
                <MenuItem value="3" sx={{ fontWeight: 600 }}>🔵 Answered ({AnsweredPrayers})</MenuItem>
                <MenuItem value="2" sx={{ fontWeight: 600 }}>🔴 Declined ({DeclinePrayers})</MenuItem>
                <MenuItem value="archived" sx={{ fontWeight: 600 }}>📦 Archived ({archivedList.length})</MenuItem>
              </Select>
            </FormControl>

            {/* Category Dropdown */}
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel sx={{ color: isLight ? '#475569' : undefined, fontWeight: 700, fontSize: "0.82rem" }}>
                Category
              </InputLabel>
              <Select
                value={filterCategory}
                label="Category"
                onChange={(e) => {
                  setFilterCategory(e.target.value);
                  setPage(1);
                }}
                sx={{
                  borderRadius: "8px",
                  bgcolor: isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.05)",
                  color: isLight ? "#0F172A" : "#FFFFFF",
                  fontWeight: 600,
                  fontSize: "0.82rem",
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: isLight ? '#CBD5E1' : 'rgba(255, 255, 255, 0.15)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0D9488' },
                }}
              >
                <MenuItem value="All Categories" sx={{ fontWeight: 600 }}>All Categories</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Visibility Filter: All Confidential, Pastoral Private, Elders Only */}
            <FormControl size="small" sx={{ minWidth: 155 }}>
              <InputLabel sx={{ color: isLight ? '#475569' : undefined, fontWeight: 700, fontSize: "0.82rem" }}>
                Visibility
              </InputLabel>
              <Select
                value={filterVisibility}
                label="Visibility"
                onChange={(e) => {
                  setFilterVisibility(e.target.value);
                  setPage(1);
                }}
                sx={{
                  borderRadius: "8px",
                  bgcolor: isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.05)",
                  color: isLight ? "#0F172A" : "#FFFFFF",
                  fontWeight: 600,
                  fontSize: "0.82rem",
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: isLight ? '#CBD5E1' : 'rgba(255, 255, 255, 0.15)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0D9488' },
                }}
              >
                <MenuItem value="all" sx={{ fontWeight: 600 }}>All Confidential</MenuItem>
                <MenuItem value="private" sx={{ fontWeight: 600 }}>🔒 Pastoral Private</MenuItem>
                <MenuItem value="elders" sx={{ fontWeight: 600 }}>👑 Elders Only</MenuItem>
              </Select>
            </FormControl>

            {/* Priority Filter */}
            <FormControl size="small" sx={{ minWidth: 135 }}>
              <InputLabel sx={{ color: isLight ? '#475569' : undefined, fontWeight: 700, fontSize: "0.82rem" }}>
                Priority
              </InputLabel>
              <Select
                value={filterPriority}
                label="Priority"
                onChange={(e) => {
                  setFilterPriority(e.target.value);
                  setPage(1);
                }}
                sx={{
                  borderRadius: "8px",
                  bgcolor: isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.05)",
                  color: isLight ? "#0F172A" : "#FFFFFF",
                  fontWeight: 600,
                  fontSize: "0.82rem",
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: isLight ? '#CBD5E1' : 'rgba(255, 255, 255, 0.15)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0D9488' },
                }}
              >
                <MenuItem value="all" sx={{ fontWeight: 600 }}>All Priorities</MenuItem>
                <MenuItem value="critical" sx={{ fontWeight: 600, color: "#EF4444" }}>🔥 Critical ({priorityCounts.critical})</MenuItem>
                <MenuItem value="high" sx={{ fontWeight: 600, color: "#F59E0B" }}>⚡ High ({priorityCounts.high})</MenuItem>
                <MenuItem value="medium" sx={{ fontWeight: 600, color: "#0D9488" }}>Medium ({priorityCounts.medium})</MenuItem>
                <MenuItem value="standard" sx={{ fontWeight: 600 }}>Standard ({priorityCounts.standard})</MenuItem>
              </Select>
            </FormControl>

            {/* Sort By Dropdown */}
            <FormControl size="small" sx={{ minWidth: 165 }}>
              <InputLabel sx={{ color: isLight ? '#475569' : undefined, fontWeight: 700, fontSize: "0.82rem" }}>
                Sort By
              </InputLabel>
              <Select
                value={sortBy}
                label="Sort By"
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setPage(1);
                }}
                sx={{
                  borderRadius: "8px",
                  bgcolor: isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.05)",
                  color: isLight ? "#0F172A" : "#FFFFFF",
                  fontWeight: 600,
                  fontSize: "0.82rem",
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: isLight ? '#CBD5E1' : 'rgba(255, 255, 255, 0.15)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0D9488' },
                }}
              >
                <MenuItem value="priority_desc" sx={{ fontWeight: 600 }}>⚡ Priority: Critical First</MenuItem>
                <MenuItem value="priority_asc" sx={{ fontWeight: 600 }}>⚡ Priority: Lowest First</MenuItem>
                <MenuItem value="date_desc" sx={{ fontWeight: 600 }}>📅 Date: Newest First</MenuItem>
                <MenuItem value="date_asc" sx={{ fontWeight: 600 }}>📅 Date: Oldest First</MenuItem>
                <MenuItem value="title_asc" sx={{ fontWeight: 600 }}>🔤 Title: A → Z</MenuItem>
                <MenuItem value="title_desc" sx={{ fontWeight: 600 }}>🔤 Title: Z → A</MenuItem>
                <MenuItem value="requester_asc" sx={{ fontWeight: 600 }}>👤 Requester: A → Z</MenuItem>
                <MenuItem value="requester_desc" sx={{ fontWeight: 600 }}>👤 Requester: Z → A</MenuItem>
                <MenuItem value="org_asc" sx={{ fontWeight: 600 }}>🏢 Organization: A → Z</MenuItem>
                <MenuItem value="status_asc" sx={{ fontWeight: 600 }}>⏳ Status: Pending First</MenuItem>
              </Select>
            </FormControl>

            {/* From Date */}
            <TextField
              size="small"
              label="From"
              type="date"
              InputLabelProps={{ shrink: true, sx: { color: isLight ? "#475569" : undefined, fontWeight: 700, fontSize: "0.82rem" } }}
              value={fromDate}
              onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
              inputProps={{ max: new Date().toISOString().split('T')[0] }}
              sx={{
                width: 125,
                '& .MuiOutlinedInput-root': {
                  borderRadius: "8px",
                  bgcolor: isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.05)",
                  color: isLight ? "#0F172A" : "#FFFFFF",
                  fontWeight: 600,
                  fontSize: "0.82rem",
                  '& fieldset': { borderColor: isLight ? '#CBD5E1' : 'rgba(255, 255, 255, 0.15)' },
                  '&:hover fieldset': { borderColor: '#0D9488' },
                }
              }}
            />

            {/* To Date */}
            <TextField
              size="small"
              label="To"
              type="date"
              InputLabelProps={{ shrink: true, sx: { color: isLight ? "#475569" : undefined, fontWeight: 700, fontSize: "0.82rem" } }}
              value={toDate}
              onChange={(e) => { setToDate(e.target.value); setPage(1); }}
              inputProps={{ max: new Date().toISOString().split('T')[0] }}
              sx={{
                width: 125,
                '& .MuiOutlinedInput-root': {
                  borderRadius: "8px",
                  bgcolor: isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.05)",
                  color: isLight ? "#0F172A" : "#FFFFFF",
                  fontWeight: 600,
                  fontSize: "0.82rem",
                  '& fieldset': { borderColor: isLight ? '#CBD5E1' : 'rgba(255, 255, 255, 0.15)' },
                  '&:hover fieldset': { borderColor: '#0D9488' },
                }
              }}
            />

            {/* Organization filter (Super Admin only) */}
            {user?.role === 1 && !isSwitched && (
              <Autocomplete
                size="small"
                value={organization}
                onChange={(event, newValue) => {
                  setOrganization(newValue);
                  setPage(1);
                }}
                options={orgOptions}
                getOptionLabel={(option) => option?.org_name || ""}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Organization"
                    InputLabelProps={{ sx: { color: isLight ? '#475569' : undefined, fontWeight: 700, fontSize: "0.82rem" } }}
                  />
                )}
                sx={{
                  width: 160,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: "8px",
                    bgcolor: isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.05)",
                    color: isLight ? "#0F172A" : "#FFFFFF",
                    fontWeight: 600,
                    fontSize: "0.82rem",
                    '& fieldset': { borderColor: isLight ? '#CBD5E1' : 'rgba(255, 255, 255, 0.15)' },
                    '&:hover fieldset': { borderColor: '#0D9488' },
                  }
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            )}

            {/* Reset Filters button */}
            {(filterCategory !== "All Categories" || filterSort !== "" || filterPriority !== "all" || sortBy !== "priority_desc" || fromDate !== "" || toDate !== "" || searchTerm !== "" || organization !== null || filterActiveTab === "archived") && (
              <Tooltip title="Reset all active filters">
                <Button
                  size="small"
                  onClick={handleResetFilters}
                  startIcon={<RestartAltIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    borderRadius: "8px",
                    px: 1.2,
                    py: 0.6,
                    color: isLight ? "#B91C1C" : "#F87171",
                    bgcolor: isLight ? "#FEF2F2" : "rgba(239, 68, 68, 0.15)",
                    border: isLight ? "1px solid #FECACA" : "1px solid rgba(239, 68, 68, 0.4)",
                    fontWeight: 700,
                    fontSize: "0.78rem",
                    textTransform: "none",
                    '&:hover': {
                      bgcolor: isLight ? "#FEE2E2" : "rgba(239, 68, 68, 0.25)",
                    }
                  }}
                >
                  Reset
                </Button>
              </Tooltip>
            )}
          </Box>
        </Paper>

        {/* ── PRIORITY-WISE LIST VIEW TABLE ── */}
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            borderRadius: "16px",
            overflow: "hidden",
            border: isLight ? "1.5px solid #CBD5E1" : "1px solid rgba(76, 161, 175, 0.25)",
            background: isLight ? "#FFFFFF" : "rgba(21, 34, 46, 0.7)",
            boxShadow: isLight ? "0 4px 16px rgba(15, 23, 42, 0.05)" : "none",
          }}
        >
          {loading ? (
            <Box py={8}>
              <SectionLoader />
            </Box>
          ) : paginatedPrayers.length === 0 ? (
            <Box py={8} textAlign="center">
              <SearchOffIcon sx={{ fontSize: 50, color: isLight ? "#94A3B8" : "rgba(255,255,255,0.3)", mb: 1.5 }} />
              <Typography variant="h6" fontWeight={800} sx={{ color: isLight ? "#0F172A" : "#FFFFFF" }}>
                {filterActiveTab === "archived" ? "No archived private prayer requests found" : "No private prayer requests match your criteria"}
              </Typography>
              <Typography variant="body2" sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.6)", mt: 0.5 }}>
                Try adjusting your search terms, priority pill, date range, or selected status.
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: "calc(100vh - 310px)", maxWidth: "100%", overflowX: "auto" }}>
              <Table stickyHeader size="small" sx={{ minWidth: { xs: 650, md: 800, lg: 950 } }}>
                <TableHead>
                  <TableRow
                    sx={{
                      '& th': {
                        bgcolor: isLight ? "#F1F5F9" : "#1a2636",
                        color: isLight ? "#1E293B" : "#F8FAFC",
                        fontWeight: 800,
                        fontSize: "0.78rem",
                        letterSpacing: "0.6px",
                        textTransform: "uppercase",
                        borderBottom: isLight ? "2px solid #CBD5E1" : "1.5px solid rgba(255,255,255,0.12)",
                        py: 1.5,
                      }
                    }}
                  >
                    <TableCell align="center" sx={{ width: 50 }}>#</TableCell>
                    <TableCell sx={{ minWidth: 130 }}>
                      <TableSortLabel
                        active={sortBy.startsWith('priority')}
                        direction={sortBy === 'priority_asc' ? 'asc' : 'desc'}
                        onClick={() => handleRequestSort('priority')}
                        sx={{
                          fontWeight: 800,
                          '&.Mui-active': { color: isLight ? '#0D9488' : '#5EEAD4' },
                          '& .MuiTableSortLabel-icon': { color: isLight ? '#0D9488 !important' : '#5EEAD4 !important' }
                        }}
                      >
                        Priority
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ minWidth: 210 }}>
                      <TableSortLabel
                        active={sortBy.startsWith('requester')}
                        direction={sortBy === 'requester_desc' ? 'desc' : 'asc'}
                        onClick={() => handleRequestSort('requester')}
                        sx={{
                          fontWeight: 800,
                          '&.Mui-active': { color: isLight ? '#0D9488' : '#5EEAD4' },
                          '& .MuiTableSortLabel-icon': { color: isLight ? '#0D9488 !important' : '#5EEAD4 !important' }
                        }}
                      >
                        Requester
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ minWidth: 290 }}>
                      <TableSortLabel
                        active={sortBy.startsWith('title')}
                        direction={sortBy === 'title_desc' ? 'desc' : 'asc'}
                        onClick={() => handleRequestSort('title')}
                        sx={{
                          fontWeight: 800,
                          '&.Mui-active': { color: isLight ? '#0D9488' : '#5EEAD4' },
                          '& .MuiTableSortLabel-icon': { color: isLight ? '#0D9488 !important' : '#5EEAD4 !important' }
                        }}
                      >
                        Prayer Details
                      </TableSortLabel>
                    </TableCell>
                    {showOrganizationColumn && (
                      <TableCell sx={{ minWidth: 150 }}>
                        <TableSortLabel
                          active={sortBy.startsWith('org')}
                          direction={sortBy === 'org_desc' ? 'desc' : 'asc'}
                          onClick={() => handleRequestSort('org')}
                          sx={{
                            fontWeight: 800,
                            '&.Mui-active': { color: isLight ? '#0D9488' : '#5EEAD4' },
                            '& .MuiTableSortLabel-icon': { color: isLight ? '#0D9488 !important' : '#5EEAD4 !important' }
                          }}
                        >
                          Organization
                        </TableSortLabel>
                      </TableCell>
                    )}
                    <TableCell sx={{ minWidth: 150 }}>
                      <TableSortLabel
                        active={sortBy.startsWith('date')}
                        direction={sortBy === 'date_asc' ? 'asc' : 'desc'}
                        onClick={() => handleRequestSort('date')}
                        sx={{
                          fontWeight: 800,
                          '&.Mui-active': { color: isLight ? '#0D9488' : '#5EEAD4' },
                          '& .MuiTableSortLabel-icon': { color: isLight ? '#0D9488 !important' : '#5EEAD4 !important' }
                        }}
                      >
                        Date & Time
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ minWidth: 130 }}>
                      <TableSortLabel
                        active={sortBy.startsWith('status')}
                        direction={sortBy === 'status_desc' ? 'desc' : 'asc'}
                        onClick={() => handleRequestSort('status')}
                        sx={{
                          fontWeight: 800,
                          '&.Mui-active': { color: isLight ? '#0D9488' : '#5EEAD4' },
                          '& .MuiTableSortLabel-icon': { color: isLight ? '#0D9488 !important' : '#5EEAD4 !important' }
                        }}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center" sx={{ width: 140 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {paginatedPrayers.map((prayer, index) => {
                    const requesterName = prayer.is_anonymous === 1 || prayer.is_anonymous === "1"
                      ? "Anonymous Requester"
                      : `${prayer.app_users?.first_name || ""} ${prayer.app_users?.last_name || ""}`.trim() || "Anonymous Requester";

                    const requesterEmail = prayer.is_anonymous === 1 || prayer.is_anonymous === "1"
                      ? "Confidential / Private"
                      : prayer.app_users?.email || "No email available";

                    const plainDescription = stripHtml(prayer.description || "");
                    const isArchived = archivedList.some(a => String(a.id) === String(prayer.id));

                    const priorityStripeColor = {
                      critical: '#EF4444',
                      high: '#F59E0B',
                      medium: '#0D9488',
                      low: '#64748B',
                    }[String(prayer.importance || '').toLowerCase().trim()] || (isLight ? '#E2E8F0' : 'rgba(255,255,255,0.1)');

                    return (
                      <TableRow
                        key={prayer.id || index}
                        hover
                        sx={{
                          cursor: "pointer",
                          transition: "background-color 0.15s ease",
                          position: "relative",
                          borderLeft: `4px solid ${priorityStripeColor}`,
                          '&:hover': {
                            bgcolor: isLight ? "#F0FDFA !important" : "rgba(13, 148, 136, 0.1) !important",
                          },
                          '& td': {
                            borderBottom: isLight ? "1px solid #E2E8F0" : "1px solid rgba(255,255,255,0.06)",
                            py: 1.35,
                            color: isLight ? "#1E293B" : "#F1F5F9",
                          }
                        }}
                        onClick={() => handleViewPrayer(prayer)}
                      >
                        {/* 1. Row Index */}
                        <TableCell align="center" sx={{ fontWeight: 700, color: isLight ? "#64748B" : "rgba(255,255,255,0.5)", fontSize: "0.82rem" }}>
                          {((page - 1) * rowsPerPage) + index + 1}
                        </TableCell>

                        {/* 2. Priority Column */}
                        <TableCell>
                          {getPriorityBadge(prayer.importance)}
                        </TableCell>

                        {/* 3. Requester Information */}
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1.4}>
                            <Avatar
                              sx={{
                                width: 38,
                                height: 38,
                                background: stringToColor(requesterName),
                                fontWeight: 800,
                                fontSize: "0.85rem",
                                color: "#FFFFFF",
                                boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                              }}
                            >
                              {getInitials(prayer.app_users?.first_name, prayer.app_users?.last_name)}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                              <Box display="flex" alignItems="center" gap={0.6}>
                                <Typography
                                  variant="body2"
                                  fontWeight={800}
                                  noWrap
                                  sx={{
                                    color: isLight ? "#0F172A" : "#FFFFFF",
                                    fontSize: "0.9rem",
                                  }}
                                >
                                  {requesterName}
                                </Typography>
                                <LockIcon sx={{ fontSize: 13, color: isLight ? "#0D9488" : "#5EEAD4" }} />
                              </Box>
                              <Typography
                                variant="caption"
                                noWrap
                                sx={{
                                  display: "block",
                                  color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.6)",
                                  fontWeight: 500,
                                  fontSize: "0.75rem",
                                }}
                              >
                                {requesterEmail}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        {/* 4. Prayer Details (Title, Category, snippet) */}
                        <TableCell>
                          <Box sx={{ pr: 1 }}>
                            <Box display="flex" alignItems="center" gap={1} mb={0.4} flexWrap="wrap">
                              <Typography
                                variant="body2"
                                fontWeight={800}
                                sx={{
                                  color: isLight ? "#0D9488" : "#5EEAD4",
                                  fontSize: "0.9rem",
                                  lineHeight: 1.25,
                                }}
                              >
                                {prayer.title || "Untitled Prayer"}
                              </Typography>
                              {prayer.categories?.name && (
                                <Chip
                                  size="small"
                                  label={prayer.categories.name}
                                  sx={{
                                    height: 20,
                                    fontSize: "0.68rem",
                                    fontWeight: 700,
                                    borderRadius: "6px",
                                    bgcolor: isLight ? "#F1F5F9" : "rgba(255, 255, 255, 0.08)",
                                    color: isLight ? "#334155" : "rgba(255, 255, 255, 0.8)",
                                    border: isLight ? "1px solid #CBD5E1" : "1px solid rgba(255, 255, 255, 0.15)",
                                  }}
                                />
                              )}
                              {prayer.checked === 2 ? (
                                <Chip
                                  size="small"
                                  label="👑 Elders Only"
                                  sx={{
                                    height: 20,
                                    fontSize: "0.68rem",
                                    fontWeight: 800,
                                    borderRadius: "6px",
                                    bgcolor: "rgba(124, 58, 237, 0.15)",
                                    color: "#7C3AED",
                                    border: "1px solid rgba(124, 58, 237, 0.35)",
                                  }}
                                />
                              ) : (
                                <Chip
                                  size="small"
                                  label="🔒 Pastoral Private"
                                  sx={{
                                    height: 20,
                                    fontSize: "0.68rem",
                                    fontWeight: 700,
                                    borderRadius: "6px",
                                    bgcolor: "rgba(13, 148, 136, 0.12)",
                                    color: "#0D9488",
                                    border: "1px solid rgba(13, 148, 136, 0.3)",
                                  }}
                                />
                              )}
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{
                                color: isLight ? "#475569" : "rgba(255, 255, 255, 0.7)",
                                fontSize: "0.82rem",
                                lineHeight: 1.35,
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {plainDescription || "No additional prayer notes provided."}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* 5. Organization */}
                        {showOrganizationColumn && (
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={0.8}>
                              <BusinessIcon sx={{ fontSize: 16, color: isLight ? "#64748B" : "rgba(255,255,255,0.4)" }} />
                              <Typography
                                variant="body2"
                                noWrap
                                fontWeight={600}
                                sx={{ color: isLight ? "#334155" : "#E2E8F0", fontSize: "0.82rem" }}
                              >
                                {prayer.origanisation?.org_name || prayer.organizations?.org_name || "Global / General"}
                              </Typography>
                            </Box>
                          </TableCell>
                        )}

                        {/* 6. Date & Time */}
                        <TableCell>
                          <Box>
                            <Box display="flex" alignItems="center" gap={0.6}>
                              <CalendarTodayIcon sx={{ fontSize: 13, color: isLight ? "#0D9488" : "#5EEAD4" }} />
                              <Typography variant="body2" fontWeight={700} sx={{ color: isLight ? "#0F172A" : "#FFFFFF", fontSize: "0.82rem" }}>
                                {prayer.created_at ? new Date(prayer.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "-"}
                              </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={0.6} mt={0.2}>
                              <AccessTimeIcon sx={{ fontSize: 13, color: isLight ? "#64748B" : "rgba(255,255,255,0.4)" }} />
                              <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.6)", fontSize: "0.72rem", fontWeight: 600 }}>
                                {prayer.created_at ? new Date(prayer.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "-"}
                              </Typography>
                            </Box>
                            {/* Admin Approval Date */}
                            {String(prayer.is_approved) === "1" && prayer.updated_at && (
                              <Box display="flex" alignItems="center" gap={0.5} mt={0.5} sx={{ bgcolor: isLight ? 'rgba(16, 185, 129, 0.08)' : 'rgba(16, 185, 129, 0.15)', px: 0.8, py: 0.25, borderRadius: 1, border: '1px solid rgba(16, 185, 129, 0.3)' }}>
                                <CheckCircleIcon sx={{ fontSize: 12, color: "#10B981" }} />
                                <Typography variant="caption" sx={{ color: "#10B981", fontSize: "0.70rem", fontWeight: 700 }}>
                                  Approved: {new Date(prayer.updated_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </TableCell>

                        {/* 7. Status */}
                        <TableCell>
                          {getStatusBadge(prayer)}
                        </TableCell>

                        {/* 8. Quick Actions */}
                        <TableCell align="center">
                          <Box display="flex" alignItems="center" justifyContent="center" gap={0.6} onClick={(e) => e.stopPropagation()}>
                            {/* View Eye */}
                            <Tooltip title="View Full Details">
                              <IconButton
                                size="small"
                                onClick={() => handleViewPrayer(prayer)}
                                sx={{
                                  color: isLight ? "#0D9488" : "#5EEAD4",
                                  bgcolor: isLight ? "#E6FFFA" : "rgba(13, 148, 136, 0.2)",
                                  border: isLight ? "1px solid #99F6E4" : "1px solid rgba(13, 148, 136, 0.4)",
                                  p: 0.7,
                                  '&:hover': {
                                    bgcolor: isLight ? "#CCFBF1" : "rgba(13, 148, 136, 0.35)",
                                  }
                                }}
                              >
                                <VisibilityIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>


                            {/* Quick Approve (Pending or Declined) */}
                            {(prayer.is_approved === "0" || prayer.is_approved === "2") && (
                              <Tooltip title="Approve Request">
                                <IconButton
                                  size="small"
                                  onClick={() => handleApprovePrayer(prayer.id, "1")}
                                  sx={{
                                    color: isLight ? "#059669" : "#34D399",
                                    bgcolor: isLight ? "#ECFDF5" : "rgba(16, 185, 129, 0.2)",
                                    border: isLight ? "1px solid #A7F3D0" : "1px solid rgba(16, 185, 129, 0.4)",
                                    p: 0.7,
                                    '&:hover': {
                                      bgcolor: isLight ? "#D1FAE5" : "rgba(16, 185, 129, 0.35)",
                                    }
                                  }}
                                >
                                  <CheckCircleIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}

                            {/* Quick Decline (Pending or Active) */}
                            {(prayer.is_approved === "0" || prayer.is_approved === "1") && (
                              <Tooltip title="Decline Request">
                                <IconButton
                                  size="small"
                                  onClick={() => handleConfirmDeclinePrayer(prayer.id)}
                                  sx={{
                                    color: isLight ? "#DC2626" : "#F87171",
                                    bgcolor: isLight ? "#FEF2F2" : "rgba(239, 68, 68, 0.2)",
                                    border: isLight ? "1px solid #FECACA" : "1px solid rgba(239, 68, 68, 0.4)",
                                    p: 0.7,
                                    '&:hover': {
                                      bgcolor: isLight ? "#FEE2E2" : "rgba(239, 68, 68, 0.35)",
                                    }
                                  }}
                                >
                                  <CancelIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}

                            {/* Archive / Restore Button */}
                            {!isArchived ? (
                              <Tooltip title="Archive Prayer">
                                <IconButton
                                  size="small"
                                  onClick={() => handleArchive(prayer)}
                                  sx={{
                                    color: isLight ? "#B45309" : "#FBBF24",
                                    bgcolor: isLight ? "#FFFBEB" : "rgba(245, 158, 11, 0.2)",
                                    border: isLight ? "1px solid #FDE68A" : "1px solid rgba(245, 158, 11, 0.4)",
                                    p: 0.7,
                                    '&:hover': {
                                      bgcolor: isLight ? "#FEF3C7" : "rgba(245, 158, 11, 0.35)",
                                    }
                                  }}
                                >
                                  <ArchiveIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <>
                                <Tooltip title="Restore Prayer">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRestore(prayer.id)}
                                    sx={{
                                      color: isLight ? "#059669" : "#34D399",
                                      bgcolor: isLight ? "#ECFDF5" : "rgba(16, 185, 129, 0.2)",
                                      border: isLight ? "1px solid #A7F3D0" : "1px solid rgba(16, 185, 129, 0.4)",
                                      p: 0.7,
                                    }}
                                  >
                                    <UnarchiveIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Permanently Delete">
                                  <IconButton
                                    size="small"
                                    onClick={() => handlePermanentDelete(prayer.id)}
                                    sx={{
                                      color: isLight ? "#DC2626" : "#F87171",
                                      bgcolor: isLight ? "#FEF2F2" : "rgba(239, 68, 68, 0.2)",
                                      border: isLight ? "1px solid #FECACA" : "1px solid rgba(239, 68, 68, 0.4)",
                                      p: 0.7,
                                    }}
                                  >
                                    <DeleteForeverIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* ── FOOTER & PAGINATION ── */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              p: 2,
              borderTop: isLight ? "1.5px solid #CBD5E1" : "1px solid rgba(255, 255, 255, 0.08)",
              bgcolor: isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.02)",
              gap: 1.5,
            }}
          >
            <Typography variant="body2" sx={{ color: isLight ? "#475569" : "rgba(255, 255, 255, 0.7)", fontWeight: 600 }}>
              Showing <strong>{paginatedPrayers.length}</strong> of <strong>{filteredAndSortedPrayers.length}</strong> {filterActiveTab === "archived" ? "archived" : ""} prayer requests (sorted by priority)
            </Typography>

            {totalDisplayPages > 1 && (
              <Pagination
                count={totalDisplayPages}
                page={page}
                onChange={(e, val) => setPage(val)}
                color="primary"
                shape="rounded"
                size="medium"
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontWeight: 700,
                    borderRadius: "8px",
                  }
                }}
              />
            )}
          </Box>
        </Paper>

        {/* ── VIEW PRAYER DETAILS MODAL ── */}
        <Dialog
          open={openViewDialog}
          onClose={handleViewDialogClose}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "16px",
              background: isLight ? "#FFFFFF" : "linear-gradient(135deg, #182232 0%, #1c2b3d 100%)",
              border: isLight ? "1.5px solid #CBD5E1" : "1px solid rgba(76, 161, 175, 0.35)",
              boxShadow: "0 20px 40px rgba(0,0,0,0.25)",
            }
          }}
        >
          {selectedPrayer && (
            <>
              <DialogTitle
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderBottom: isLight ? "1.5px solid #E2E8F0" : "1px solid rgba(255, 255, 255, 0.08)",
                  pb: 2,
                }}
              >
                <Box display="flex" alignItems="center" gap={1.2}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "10px",
                      bgcolor: isLight ? "#E6FFFA" : "rgba(13, 148, 136, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#0D9488",
                    }}
                  >
                    <LockIcon />
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={800} sx={{ color: isLight ? "#0F172A" : "#FFFFFF" }}>
                      Private Prayer Request Details
                    </Typography>
                  </Box>
                </Box>
                <IconButton onClick={handleViewDialogClose} size="small">
                  <CloseIcon />
                </IconButton>
              </DialogTitle>

              <DialogContent sx={{ p: 3, pt: "20px !important" }}>
                {/* 1. Requester & Priority Card */}
                <Box
                  sx={{
                    p: 2,
                    mb: 2,
                    borderRadius: "12px",
                    bgcolor: isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.04)",
                    border: isLight ? "1px solid #E2E8F0" : "1px solid rgba(255, 255, 255, 0.08)",
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Avatar
                      sx={{
                        width: 48,
                        height: 48,
                        background: stringToColor(selectedPrayer.app_users?.first_name),
                        fontWeight: 800,
                      }}
                    >
                      {getInitials(selectedPrayer.app_users?.first_name, selectedPrayer.app_users?.last_name)}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={800} sx={{ color: isLight ? "#0F172A" : "#FFFFFF", lineHeight: 1.2, mb: 0.3 }}>
                        {selectedPrayer.is_anonymous === 1 || selectedPrayer.is_anonymous === "1"
                          ? "Anonymous Requester"
                          : `${selectedPrayer.app_users?.first_name || ""} ${selectedPrayer.app_users?.last_name || ""}`.trim() || "Anonymous"}
                      </Typography>
                      
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <EmailIcon sx={{ fontSize: 13, color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.6)" }} />
                          <Typography variant="caption" sx={{ color: isLight ? "#475569" : "rgba(255, 255, 255, 0.8)", fontWeight: 600 }}>
                            {selectedPrayer.is_anonymous === 1 || selectedPrayer.is_anonymous === "1"
                              ? "Confidential / Private Request"
                              : selectedPrayer.app_users?.email || "No email available"}
                          </Typography>
                        </Box>

                        {selectedPrayer.app_users?.phone && (
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Typography variant="caption" sx={{ color: isLight ? "#CBD5E1" : "rgba(255,255,255,0.3)" }}>•</Typography>
                            <PhoneIcon sx={{ fontSize: 13, color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.6)" }} />
                            <Typography variant="caption" sx={{ color: isLight ? "#475569" : "rgba(255, 255, 255, 0.8)", fontWeight: 600 }}>
                              {selectedPrayer.app_users.phone}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="center" gap={1.2}>
                    <Typography variant="caption" fontWeight={700} sx={{ color: isLight ? "#475569" : "rgba(255,255,255,0.7)" }}>
                      PRIORITY:
                    </Typography>
                    <Select
                      size="small"
                      value={modalPriority}
                      onChange={(e) => handleUpdatePriority(selectedPrayer.id, e.target.value)}
                      sx={{
                        borderRadius: "8px",
                        fontSize: "0.82rem",
                        fontWeight: 700,
                        bgcolor: isLight ? "#FFFFFF" : "rgba(255,255,255,0.08)",
                        height: 34,
                      }}
                    >
                      <MenuItem value="low">Low</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                      <MenuItem value="critical">Critical</MenuItem>
                    </Select>
                  </Box>
                </Box>

                {/* 2. Structured Metadata & Audience Grid */}
                <Box
                  sx={{
                    p: 1.8,
                    mb: 2.5,
                    borderRadius: "12px",
                    bgcolor: isLight ? "#F1F5F9" : "rgba(255, 255, 255, 0.03)",
                    border: isLight ? "1px solid #E2E8F0" : "1px solid rgba(255, 255, 255, 0.08)",
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 1.5,
                  }}
                >
                  {/* Visibility / Target Audience */}
                  <Box>
                    <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.6)", fontWeight: 700, textTransform: "uppercase", fontSize: "0.7rem", display: "block", mb: 0.5 }}>
                      Target Audience / Visibility
                    </Typography>
                    {selectedPrayer.checked === 2 ? (
                      <Chip
                        icon={<SecurityIcon sx={{ fontSize: "16px !important", color: "#7C3AED !important" }} />}
                        label="👑 Only for Elders (Confidential)"
                        sx={{
                          fontWeight: 800,
                          fontSize: "0.78rem",
                          bgcolor: isLight ? "#F3E8FF" : "rgba(124, 58, 237, 0.2)",
                          color: isLight ? "#6D28D9" : "#C4B5FD",
                          border: "1px solid rgba(124, 58, 237, 0.35)",
                        }}
                      />
                    ) : selectedPrayer.checked === 1 ? (
                      <Chip
                        icon={<LockIcon sx={{ fontSize: "15px !important", color: "#0D9488 !important" }} />}
                        label="🔒 Pastoral Private (Pastor & Admin)"
                        sx={{
                          fontWeight: 800,
                          fontSize: "0.78rem",
                          bgcolor: isLight ? "#CCFBF1" : "rgba(13, 148, 136, 0.2)",
                          color: isLight ? "#0F766E" : "#5EEAD4",
                          border: "1px solid rgba(13, 148, 136, 0.35)",
                        }}
                      />
                    ) : (
                      <Chip
                        icon={<PublicIcon sx={{ fontSize: "15px !important", color: "#2563EB !important" }} />}
                        label="🌐 Public Prayer (All Members)"
                        sx={{
                          fontWeight: 800,
                          fontSize: "0.78rem",
                          bgcolor: isLight ? "#DBEAFE" : "rgba(37, 99, 235, 0.2)",
                          color: isLight ? "#1D4ED8" : "#93C5FD",
                          border: "1px solid rgba(37, 99, 235, 0.35)",
                        }}
                      />
                    )}
                  </Box>

                  {/* Church / Organization */}
                  {showOrganizationColumn && (
                    <Box>
                      <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.6)", fontWeight: 700, textTransform: "uppercase", fontSize: "0.7rem", display: "block", mb: 0.5 }}>
                        Church / Organization
                      </Typography>
                      <Box display="flex" alignItems="center" gap={0.8} sx={{ height: 32 }}>
                        <BusinessIcon sx={{ fontSize: 18, color: isLight ? "#0D9488" : "#5EEAD4" }} />
                        <Typography variant="body2" fontWeight={700} sx={{ color: isLight ? "#1E293B" : "#F1F5F9" }}>
                          {selectedPrayer.origanisation?.org_name || "Way Truth Life Church"}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Category */}
                  <Box>
                    <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.6)", fontWeight: 700, textTransform: "uppercase", fontSize: "0.7rem", display: "block", mb: 0.5 }}>
                      Category
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.8} sx={{ height: 32 }}>
                      <CategoryIcon sx={{ fontSize: 18, color: isLight ? "#0D9488" : "#5EEAD4" }} />
                      <Typography variant="body2" fontWeight={700} sx={{ color: isLight ? "#1E293B" : "#F1F5F9" }}>
                        {selectedPrayer.categories?.name || "General"}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Date & Time */}
                  <Box>
                    <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.6)", fontWeight: 700, textTransform: "uppercase", fontSize: "0.7rem", display: "block", mb: 0.5 }}>
                      Submitted Date & Time
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.8} sx={{ height: 32 }}>
                      <CalendarTodayIcon sx={{ fontSize: 17, color: isLight ? "#0D9488" : "#5EEAD4" }} />
                      <Typography variant="body2" fontWeight={600} sx={{ color: isLight ? "#334155" : "rgba(255,255,255,0.85)" }}>
                        {selectedPrayer.created_at ? new Date(selectedPrayer.created_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : "N/A"}
                      </Typography>
                    </Box>
                    {String(selectedPrayer.is_approved) === "1" && selectedPrayer.updated_at && (
                      <Box display="flex" alignItems="center" gap={0.6} mt={0.5}>
                        <CheckCircleIcon sx={{ fontSize: 14, color: "#10B981" }} />
                        <Typography variant="caption" sx={{ color: "#10B981", fontWeight: 700 }}>
                          Admin Approved: {new Date(selectedPrayer.updated_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* 3. Prayer Subject */}
                <Box mb={2}>
                  <Box display="flex" alignItems="center" gap={1} mb={0.8}>
                    <Typography variant="h6" fontWeight={800} sx={{ color: isLight ? "#0D9488" : "#5EEAD4" }}>
                      {selectedPrayer.title || "Untitled Prayer Request"}
                    </Typography>
                    {selectedPrayer.categories?.name && (
                      <Chip
                        size="small"
                        label={selectedPrayer.categories.name}
                        sx={{
                          fontWeight: 700,
                          borderRadius: "6px",
                          bgcolor: isLight ? "#E6FFFA" : "rgba(13, 148, 136, 0.2)",
                          color: "#0D9488",
                        }}
                      />
                    )}
                  </Box>
                </Box>

                <Typography variant="caption" fontWeight={800} sx={{ color: isLight ? "#475569" : "rgba(255, 255, 255, 0.6)", letterSpacing: "0.5px", textTransform: "uppercase" }}>
                  PRAYER PETITION & DESCRIPTION
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    mt: 1,
                    mb: 3,
                    borderRadius: "12px",
                    bgcolor: isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.02)",
                    border: isLight ? "1px solid #E2E8F0" : "1px solid rgba(255, 255, 255, 0.08)",
                    color: isLight ? "#0F172A" : "#F8FAFC",
                    fontSize: "0.95rem",
                    lineHeight: 1.65,
                    maxHeight: 280,
                    overflowY: "auto",
                  }}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedPrayer.description || "<em>No content provided</em>") }}
                />

                <Box
                  sx={{
                    p: 2,
                    borderRadius: "12px",
                    bgcolor: isLight ? "#F1F5F9" : "rgba(255, 255, 255, 0.05)",
                    border: isLight ? "1px solid #CBD5E1" : "1px solid rgba(255, 255, 255, 0.08)",
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                  }}
                >
                  <Box>
                    <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.5)", fontWeight: 700, textTransform: "uppercase" }}>
                      Current Status:
                    </Typography>
                    <Box mt={0.3}>
                      {getStatusBadge(selectedPrayer)}
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="center" gap={1.2} flexWrap="wrap">
                    {(selectedPrayer.is_approved === "0" || selectedPrayer.is_approved === "2") && (
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                        onClick={() => {
                          handleApprovePrayer(selectedPrayer.id, "1");
                          handleViewDialogClose();
                        }}
                        sx={{ borderRadius: "8px", fontWeight: 700, textTransform: "none" }}
                      >
                        Approve
                      </Button>
                    )}

                    {(selectedPrayer.is_approved === "0" || selectedPrayer.is_approved === "1") && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<CancelIcon sx={{ fontSize: 16 }} />}
                        onClick={() => handleConfirmDeclinePrayer(selectedPrayer.id)}
                        sx={{ borderRadius: "8px", fontWeight: 700, textTransform: "none" }}
                      >
                        Decline
                      </Button>
                    )}

                    {!archivedList.some(a => String(a.id) === String(selectedPrayer.id)) ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ArchiveIcon sx={{ fontSize: 16 }} />}
                        onClick={() => handleArchive(selectedPrayer)}
                        sx={{
                          color: isLight ? "#B45309" : "#FBBF24",
                          borderColor: isLight ? "#FDE68A" : "rgba(245, 158, 11, 0.4)",
                          borderRadius: "8px",
                          fontWeight: 700,
                          textTransform: "none",
                        }}
                      >
                        Archive
                      </Button>
                    ) : (
                      <>
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          startIcon={<UnarchiveIcon sx={{ fontSize: 16 }} />}
                          onClick={() => {
                            handleRestore(selectedPrayer.id);
                            handleViewDialogClose();
                          }}
                          sx={{ borderRadius: "8px", fontWeight: 700, textTransform: "none" }}
                        >
                          Restore
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteForeverIcon sx={{ fontSize: 16 }} />}
                          onClick={() => handlePermanentDelete(selectedPrayer.id)}
                          sx={{ borderRadius: "8px", fontWeight: 700, textTransform: "none" }}
                        >
                          Delete Permanently
                        </Button>
                      </>
                    )}
                  </Box>
                </Box>
              </DialogContent>

              <DialogActions sx={{ p: 2.5, borderTop: isLight ? "1px solid #E2E8F0" : "1px solid rgba(255, 255, 255, 0.08)", display: "flex", justifyContent: "flex-end" }}>
                <Button
                  onClick={handleViewDialogClose}
                  variant="outlined"
                  sx={{
                    borderRadius: "10px",
                    fontWeight: 700,
                    textTransform: "none",
                    px: 3,
                  }}
                >
                  Close
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* ── SUBMIT PRIVATE PRAYER REQUEST MODAL ── */}
        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "16px",
              background: isLight ? "#FFFFFF" : "linear-gradient(135deg, #182232 0%, #1c2b3d 100%)",
              border: isLight ? "1.5px solid #CBD5E1" : "1px solid rgba(76, 161, 175, 0.35)",
              boxShadow: "0 20px 40px rgba(0,0,0,0.25)",
            }
          }}
        >
          <form onSubmit={handleSubmit}>
            <DialogTitle
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: isLight ? "1.5px solid #E2E8F0" : "1px solid rgba(255, 255, 255, 0.08)",
                pb: 2,
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <LockIcon sx={{ color: "#0D9488", fontSize: 22 }} />
                <Typography variant="h6" fontWeight={800} sx={{ color: isLight ? "#0F172A" : "#FFFFFF" }}>
                  Add Private Prayer Request
                </Typography>
              </Box>
              <IconButton onClick={() => setOpenDialog(false)} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 3, pt: "20px !important" }}>
              {formError && (
                <Box mb={2} p={1.5} borderRadius="8px" bgcolor="#FEF2F2" border="1px solid #FECACA" color="#B91C1C">
                  <Typography variant="body2" fontWeight={600}>{formError}</Typography>
                </Box>
              )}

              <Box display="flex" flexDirection="column" gap={2.2}>
                <TextField
                  fullWidth
                  label="Prayer Request Title"
                  required
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Confidential Pastoral Petition"
                />

                <Box display="flex" gap={2} flexWrap="wrap">
                  <FormControl fullWidth sx={{ flex: 1, minWidth: 200 }} required>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={category}
                      label="Category"
                      onChange={(e) => setCategory(e.target.value)}
                    >
                      {categories.map((c) => (
                        <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth sx={{ flex: 1, minWidth: 160 }}>
                    <InputLabel>Priority</InputLabel>
                    <Select
                      value={priority}
                      label="Priority"
                      onChange={(e) => setPriority(e.target.value)}
                    >
                      <MenuItem value="low">Low</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                      <MenuItem value="critical">Critical</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <FormControl fullWidth>
                  <InputLabel>Confidentiality Level</InputLabel>
                  <Select
                    value={privateAudience}
                    label="Confidentiality Level"
                    onChange={(e) => setPrivateAudience(e.target.value)}
                  >
                    <MenuItem value="private">🔒 Pastoral Private (Pastoral Team Only)</MenuItem>
                    <MenuItem value="elders">👑 Elders Only (Restricted to Church Elders)</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Detailed Prayer Description"
                  multiline
                  rows={4}
                  required
                  value={requestText}
                  onChange={(e) => setRequestText(e.target.value)}
                  placeholder="Enter confidential details of your prayer petition..."
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={submitAnon}
                      onChange={(e) => setSubmitAnon(e.target.checked)}
                      sx={{ color: "#0D9488", '&.Mui-checked': { color: "#0D9488" } }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: isLight ? "#475569" : "rgba(255,255,255,0.8)", fontWeight: 600 }}>
                      Submit anonymously (hide requester identity)
                    </Typography>
                  }
                />
              </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2.5, borderTop: isLight ? "1px solid #E2E8F0" : "1px solid rgba(255, 255, 255, 0.08)" }}>
              <Button onClick={() => setOpenDialog(false)} variant="outlined" sx={{ borderRadius: "10px", fontWeight: 700, textTransform: "none" }}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" sx={{ borderRadius: "10px", fontWeight: 700, textTransform: "none", bgcolor: "#0D9488", px: 3, '&:hover': { bgcolor: "#0F766E" } }}>
                Submit Private Prayer
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Feedback Toast */}
        <Toast toast={toast} setToast={setToast} />

        {/* Confirmation Modal */}
        <ConfirmDialog
          open={confirmDialog.open}
          title={confirmDialog.title}
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
          severity="error"
        />

      </Box>
    </>
  );
}
